import { useCallback, useMemo } from 'react';
import { getRouteApi } from '@tanstack/react-router';
import type { SortDirection, TableSort } from '@/components/common/DataTable';
import { useFilterDraft } from '@/hooks/useFilterDraft';
import { clearDependentFilterValues, parseNumberFilter } from '@/utils/filters';
import {
  ACTIVITY_STATUS,
  MO_TRAVEL_LIST_PAGE_SIZE,
  MO_TRAVEL_SORT_COLUMNS,
  type ActivityStatus,
  type MoTravelSortColumn,
} from '../constants/moTravel.constants';
import type {
  MoTravelFilterParams,
  MoTravelListParams,
  MoTravelListSearch,
  MoTravelPeriodParams,
} from '../types/moTravel.types';
import { toFullNameParam } from '../utils/toFullNameParam';

const moTravelHistoryRouteApi = getRouteApi('/_auth/geo-location/mo-travel-history/');

const DEFAULT_SORT_DIRECTION: SortDirection = 'asc';

const TEXT_FILTER_MAX_LENGTH = {
  hrId: 50,
  username: 100,
  mobile: 20,
} as const;

interface MoTravelFilterValues extends Record<string, string> {
  userTypeId: string;
  companyId: string;
  departmentId: string;
  designationId: string;
  hrId: string;
  username: string;
  mobile: string;
  activityStatus: string;
}

const EMPTY_FILTERS: MoTravelFilterValues = {
  userTypeId: '',
  companyId: '',
  departmentId: '',
  designationId: '',
  hrId: '',
  username: '',
  mobile: '',
  activityStatus: '',
};

const toOptionalIdText = (id: number | undefined): string => (id === undefined ? '' : String(id));

function toFilterValues(search: Partial<MoTravelListSearch>): MoTravelFilterValues {
  return {
    userTypeId: toOptionalIdText(search.userTypeId),
    companyId: toOptionalIdText(search.companyId),
    departmentId: toOptionalIdText(search.departmentId),
    designationId: toOptionalIdText(search.designationId),
    hrId: search.hrId ?? '',
    username: search.username ?? '',
    mobile: search.mobile ?? '',
    activityStatus: search.activityStatus ?? '',
  };
}

const toTrimmedFilterText = (text: string, maxLength: number): string | undefined =>
  text.trim().slice(0, maxLength) || undefined;

const toActivityStatus = (text: string): ActivityStatus | undefined =>
  ACTIVITY_STATUS.find((status) => status === text);

const toMoTravelSortColumn = (columnId: string | undefined): MoTravelSortColumn | undefined =>
  MO_TRAVEL_SORT_COLUMNS.find((column) => column === columnId);

function toSearchFilters(values: MoTravelFilterValues): Partial<MoTravelListSearch> {
  return {
    userTypeId: parseNumberFilter(values.userTypeId),
    companyId: parseNumberFilter(values.companyId),
    departmentId: parseNumberFilter(values.departmentId),
    designationId: parseNumberFilter(values.designationId),
    hrId: toTrimmedFilterText(values.hrId, TEXT_FILTER_MAX_LENGTH.hrId),
    username: toTrimmedFilterText(values.username, TEXT_FILTER_MAX_LENGTH.username),
    mobile: toTrimmedFilterText(values.mobile, TEXT_FILTER_MAX_LENGTH.mobile),
    activityStatus: toActivityStatus(values.activityStatus),
  };
}

const normalizeFilterValues = (values: MoTravelFilterValues): MoTravelFilterValues =>
  toFilterValues(toSearchFilters(values));

const toNextFilterValues = (
  previousFilters: MoTravelFilterValues,
  nextFilters: Record<string, string>,
): MoTravelFilterValues =>
  clearDependentFilterValues(previousFilters, { ...EMPTY_FILTERS, ...nextFilters });

export function useMoTravelListSearch() {
  const search = moTravelHistoryRouteApi.useSearch();
  const navigate = moTravelHistoryRouteApi.useNavigate();

  const appliedFilters = useMemo(() => toFilterValues(search), [search]);
  const {
    draftValues: draftFilters,
    setDraftValues: setDraftFilters,
    applyValues,
    hasPendingChanges,
  } = useFilterDraft({
    appliedValues: appliedFilters,
    normalizeValues: normalizeFilterValues,
    shouldWaitForApply: true,
    onApply: (filters) => {
      void navigate({ search: (previous) => ({ ...previous, ...toSearchFilters(filters) }) });
    },
  });

  const period = useMemo<MoTravelPeriodParams>(
    () => ({ date: search.date, mapOn: search.mapOn }),
    [search.date, search.mapOn],
  );

  const statsParams = useMemo<MoTravelFilterParams>(
    () => ({
      date: search.date,
      mapOn: search.mapOn,
      userTypeId: search.userTypeId,
      companyId: search.companyId,
      departmentId: search.departmentId,
      designationId: search.designationId,
      hrId: search.hrId,
      username: search.username,
      mobile: search.mobile,
      fullName: search.fullName,
    }),
    [search],
  );

  const listParams = useMemo<MoTravelListParams>(
    () => ({
      ...statsParams,
      activityStatus: search.activityStatus,
      sortBy: search.sortBy,
      sortDirection: search.sortDirection,
      pageSize: MO_TRAVEL_LIST_PAGE_SIZE,
    }),
    [statsParams, search.activityStatus, search.sortBy, search.sortDirection],
  );

  const tableSort = useMemo<TableSort | undefined>(
    () =>
      search.sortBy
        ? { columnId: search.sortBy, direction: search.sortDirection ?? DEFAULT_SORT_DIRECTION }
        : undefined,
    [search.sortBy, search.sortDirection],
  );

  const activeFilterCount = Object.values(appliedFilters).filter(Boolean).length;

  const handleFilterChange = useCallback(
    (nextFilters: Record<string, string>) => {
      setDraftFilters((previousFilters) => toNextFilterValues(previousFilters, nextFilters));
    },
    [setDraftFilters],
  );

  function applyFilterValues(nextFilters: Record<string, string>) {
    applyValues(toNextFilterValues(draftFilters, nextFilters));
  }

  const handleNameSearch = useCallback(
    (nameText: string) => {
      const fullName = toFullNameParam(nameText);
      void navigate({
        search: (previous) =>
          previous.fullName === fullName ? previous : { ...previous, fullName },
      });
    },
    [navigate],
  );

  const handleSortChange = useCallback(
    (nextSort: TableSort | undefined) => {
      void navigate({
        search: (previous) => ({
          ...previous,
          sortBy: toMoTravelSortColumn(nextSort?.columnId),
          sortDirection: nextSort?.direction,
        }),
        replace: true,
      });
    },
    [navigate],
  );

  const handlePeriodChange = useCallback(
    (periodChange: Partial<MoTravelPeriodParams>) => {
      void navigate({ search: (previous) => ({ ...previous, ...periodChange }) });
    },
    [navigate],
  );

  return {
    search,
    period,
    statsParams,
    listParams,
    draftFilters,
    activeFilterCount,
    hasPendingChanges,
    handleFilterChange,
    applyFilterValues,
    handleNameSearch,
    handlePeriodChange,
    tableSort,
    handleSortChange,
  };
}

export type MoTravelListSearchState = ReturnType<typeof useMoTravelListSearch>;
